import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/services/studio/client";
import { useSkillsRequestContext } from "@/services/skills";
import type {
  Estimate,
  RecommendationsResponse,
  RunDetailResponse,
  RunListResponse,
  Skill,
  SkillListResponse,
} from "@/services/studio/types";
import {
  normalizePlaybookDetailResponse,
  normalizePlaybookListResponse,
  normalizePlaybookRunDetailResponse,
  normalizePlaybookRunListResponse,
} from "@/services/migrations/normalize";

export function useSkills() {
  const ctx = useSkillsRequestContext();
  return useQuery<SkillListResponse>({
    queryKey: ["skills", ctx.orgId, ctx.userId],
    queryFn: async () => normalizePlaybookListResponse(await apiGet<unknown>("/api/playbooks")),
    enabled: Boolean(ctx.orgId),
    // Mutations (create/delete) call invalidateQueries on success.
    // Sidebar badge count doesn't benefit from background refetch every 30 s.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useSkill(id: string | undefined) {
  const ctx = useSkillsRequestContext();
  return useQuery<Skill>({
    queryKey: ["skill", id, ctx.orgId, ctx.userId],
    queryFn: async () => normalizePlaybookDetailResponse(await apiGet<unknown>(`/api/playbooks/${encodeURIComponent(id!)}`)),
    enabled: Boolean(id && ctx.orgId),
    staleTime: 30_000,
    retry: false,
  });
}

export function useSkillSource(id: string | undefined) {
  const ctx = useSkillsRequestContext();
  return useQuery<string>({
    queryKey: ["skill-source", id, ctx.orgId, ctx.userId],
    queryFn: async () => normalizePlaybookDetailResponse(await apiGet<unknown>(`/api/playbooks/${encodeURIComponent(id!)}`)).prompt ?? "",
    enabled: Boolean(id && ctx.orgId),
    staleTime: 60_000,
    retry: false,
  });
}


export interface UseRunsOptions {
  skill_id?: string;
  limit?: number;
}

export function useRuns(opts: UseRunsOptions = {}) {
  const ctx = useSkillsRequestContext();
  const params = new URLSearchParams();
  if (opts.skill_id) params.set("skill_id", opts.skill_id);
  params.set("limit", String(opts.limit ?? 50));
  const search = params.toString();
  return useQuery<RunListResponse>({
    queryKey: ["runs", opts.skill_id ?? null, opts.limit ?? 50, ctx.orgId, ctx.userId],
    queryFn: async () => normalizePlaybookRunListResponse(await apiGet<unknown>(`/api/playbook-runs?${search}`)),
    enabled: Boolean(ctx.orgId),
    staleTime: 30_000,
    retry: false,
  });
}

export function useRun(run_id: string | undefined) {
  const ctx = useSkillsRequestContext();
  return useQuery<RunDetailResponse>({
    queryKey: ["run", run_id, ctx.orgId, ctx.userId],
    queryFn: async () => normalizePlaybookRunDetailResponse(await apiGet<unknown>(`/api/playbook-runs/${encodeURIComponent(run_id!)}`)),
    enabled: Boolean(run_id && ctx.orgId),
    staleTime: 30_000,
    retry: false,
  });
}

export function useCreateSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; input: string; category?: string; prompt?: string; produces_code?: boolean }) =>
      apiPost<{ playbook: Skill }>("/api/playbooks", body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["skills"] });
    },
  });
}

export function useStopRun() {
  const queryClient = useQueryClient();
  const ctx = useSkillsRequestContext();
  return useMutation({
    mutationFn: (runId: string) => apiPost<RunDetailResponse>(`/api/playbook-runs/${encodeURIComponent(runId)}/stop`, {}),
    onSuccess: (_data, runId) => {
      void queryClient.invalidateQueries({ queryKey: ["run", runId, ctx.orgId, ctx.userId] });
      void queryClient.invalidateQueries({ queryKey: ["runs"] });
    },
  });
}

export function useRerunRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => apiPost<{ ok: boolean; previous_run_id: string; template_id: string; inputs: Record<string, unknown>; status: string }>(`/api/playbook-runs/${encodeURIComponent(runId)}/rerun`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["runs"] });
    },
  });
}


export function useRecommendations() {
  const ctx = useSkillsRequestContext();
  return useQuery<RecommendationsResponse>({
    queryKey: ["recommendations", ctx.orgId, ctx.userId],
    queryFn: async () => {
      const skills = normalizePlaybookListResponse(await apiGet<unknown>("/api/playbooks")).skills;
      return {
        recommendations: skills.slice(0, 6).map((skill, index) => ({
          skill_id: skill.id,
          title: skill.title,
          category: skill.category,
          score: Number((0.95 - index * 0.04).toFixed(2)),
          reason: skill.description,
        })),
      };
    },
    enabled: Boolean(ctx.orgId),
    staleTime: 60_000,
    retry: false,
  });
}

export function useEstimate(skill_id: string | undefined) {
  const ctx = useSkillsRequestContext();
  return useQuery<Estimate>({
    queryKey: ["estimate", skill_id, ctx.orgId, ctx.userId],
    queryFn: async () => {
      const runs = normalizePlaybookRunListResponse(
        await apiGet<unknown>(`/api/playbook-runs?skill_id=${encodeURIComponent(skill_id!)}&limit=20`)
      ).runs;
      const completed = runs.filter((run) => typeof run.total_ms === "number" && (run.total_ms ?? 0) > 0);
      const sorted = completed.map((run) => Number(run.total_ms)).sort((a, b) => a - b);
      const p50 = sorted.length ? sorted[Math.floor(sorted.length * 0.5)] : 12_000;
      const p95 = sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))] : 45_000;
      return {
        skill_id: skill_id!,
        est_latency_ms_p50: p50,
        est_latency_ms_p95: p95,
        est_cost_usd: 0,
        last_run_at: runs[0]?.started_at ?? null,
        recent_run_count: runs.length,
        sample_size: runs.length,
        source: runs.length ? "playground-history" : "heuristic",
      };
    },
    enabled: Boolean(skill_id && ctx.orgId),
    staleTime: 60_000,
    retry: false,
  });
}
